/**
 * ScParticipantTargetsCard (026, US3) — post-lock bridge card listing the
 * participant targets for the upcoming research phase. Presentational only.
 */

import { Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ChatCard } from '../../widgets/chat-widget-ui';
import { capitalizeFirst } from '../../widgets/textFormat';
import { SkBar } from '../../widgets/skeletons';
import { ScStateBadge } from './ScStateBadge';
import type { ScParticipantTargetsCardProps } from '@shared/widget-schemas';

interface ScParticipantTargetsCardComponentProps extends ScParticipantTargetsCardProps {
  className?: string;
}

export function ScParticipantTargetsCard({ targets, total, draft, className }: ScParticipantTargetsCardComponentProps) {
  const displayTotal = total ?? targets.reduce((sum, t) => sum + t.count, 0);

  return (
    <ChatCard className={cn('flex flex-col gap-3 p-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Users className="size-3.5 text-[#C084FC]" aria-hidden="true" />
          <span className="text-muted-foreground font-mono text-[10.5px] font-bold uppercase tracking-wide">
            Participant targets
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ScStateBadge draft={draft} />
          <span className="font-mono text-[13px] font-bold text-[#C084FC]">{displayTotal}</span>
        </div>
      </div>

      {/* Target rows */}
      <ul className="flex flex-col gap-2">
        {targets.map((target, i) => (
          <li
            key={i}
            className="flex items-center justify-between rounded-xl border border-[var(--border)] bg-[var(--secondary)] px-3 py-2.5"
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-foreground text-[13px] font-medium">{capitalizeFirst(target.role)}</span>
              <span className="text-muted-foreground text-[11px]">{capitalizeFirst(target.segment)}</span>
            </div>
            <span className="font-mono text-[13px] font-semibold text-[#C084FC]">
              {target.count}
            </span>
          </li>
        ))}
      </ul>
    </ChatCard>
  );
}

export function ScParticipantTargetsCardSkeleton({ className }: { className?: string }) {
  return (
    <ChatCard className={cn('flex flex-col gap-3 p-4', className)}>
      <div className="flex justify-between">
        <SkBar width="40%" height="10px" />
        <SkBar width="10%" height="10px" />
      </div>
      {[1, 2, 3].map((i) => (
        <SkBar key={i} height="52px" radius="12px" />
      ))}
    </ChatCard>
  );
}
